/* eslint-disable no-plusplus */
/* eslint-disable radix */
/* eslint-disable indent */
import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Form, message, Modal, Select } from 'antd-v5';
import { useQuery } from 'react-query';
import { connect } from 'react-redux';
import { formatISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { TZDate } from '@date-fns/tz';
import dayjs from 'dayjs';
import TextArea from 'antd-v5/es/input/TextArea';
import {
  APPOINTMENTS_STATUS,
  createAppointment,
  getAppointmentsConfig,
  updateAppointment,
} from '../../../../../../services/appointments';

const week = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const AddEditAppointment = ({
  isOpen,
  onClose,
  onSuccess,
  allAssoc = [],
  userId,
  clientId,
  appointment,
  storesIds,
}) => {
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [storeSearch, setStoreSearch] = useState('');
  const [typeSearch, setTypeSearch] = useState('');
  const [statusSearch, setStatusSearch] = useState('');
  const [form] = Form.useForm();
  const storeId = Form.useWatch('storeId', form);
  const appointmentDate = Form.useWatch('appointmentDate', form);

  const { data: appointmentsConfig, isLoading } = useQuery(
    ['appointments-config'],
    async () => {
      return getAppointmentsConfig();
    },
    {
      refetchOnWindowFocus: false,
      initialData: {
        stores: [],
        appointmentTypes: [],
      },
    },
  );

  const appointmentStore = useMemo(() => {
    const app = appointmentsConfig?.stores?.find(st => st.id === storeId);
    return app || null;
  }, [storeId, appointmentsConfig]);

  const handleFinish = async values => {
    try {
      setConfirmLoading(true);
      const timeInUTC = values.appointmentTime ? new TZDate(values.appointmentTime, appointmentStore?.timezone) : null;
      if (!timeInUTC) {
        throw new Error('Appointment time is required');
      }
      const dateTime = formatISO(
        new TZDate(
          timeInUTC.getUTCFullYear(),
          timeInUTC.getUTCMonth(),
          timeInUTC.getUTCDate(),
          timeInUTC.getUTCHours(),
          timeInUTC.getUTCMinutes(),
          'UTC',
        ),
      );

      const data = {
        storeId: values.storeId,
        associateId: values.associateId,
        appointmentTypeId: values.appointmentTypeId,
        appointmentDate: dateTime,
        note: values.note,
      };

      if (appointment) {
        data.status = values.statusId;
        await updateAppointment(appointment.id, data, true);
      } else {
        await createAppointment({
          clientId,
          data,
          loadingStatusOff: true,
        });
      }
      await onSuccess(values);
      form.resetFields();
      message.success('Appointment successfully saved');
      onClose();
    } catch (error) {
      console.error('Error saving appointment:', error);
      message.error('Error saving appointment');
    } finally {
      setConfirmLoading(false);
    }
  };
  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const disabledDays = useMemo(() => {
    if (!appointmentStore) return [];
    const disabled = [];
    Object.keys(appointmentStore?.hours || {}).forEach(day => {
      switch (day) {
        case 'mondayOpen':
          if (!appointmentStore.hours.mondayOpen || !appointmentStore.hours.mondayClose) {
            disabled.push(1); // Monday
          }
          break;
        case 'tuesdayOpen':
          if (!appointmentStore.hours.tuesdayOpen || !appointmentStore.hours.tuesdayClose) {
            disabled.push(2); // Tuesday
          }
          break;
        case 'wednesdayOpen':
          if (!appointmentStore.hours.wednesdayOpen || !appointmentStore.hours.wednesdayClose) {
            disabled.push(3); // Wednesday
          }
          break;
        case 'thursdayOpen':
          if (!appointmentStore.hours.thursdayOpen || !appointmentStore.hours.thursdayClose) {
            disabled.push(4); // Thursday
          }
          break;
        case 'fridayOpen':
          if (!appointmentStore.hours.fridayOpen || !appointmentStore.hours.fridayClose) {
            disabled.push(5); // Friday
          }
          break;
        case 'saturdayOpen':
          if (!appointmentStore.hours.saturdayOpen || !appointmentStore.hours.saturdayClose) {
            disabled.push(6); // Saturday
          }
          break;
        case 'sundayOpen':
          if (!appointmentStore.hours.sundayOpen || !appointmentStore.hours.sundayClose) {
            disabled.push(0); // Sunday
          }
          break;
        default:
          break;
      }
    });
    return disabled;
  }, [appointmentStore]);

  const appointmentSource = useMemo(() => {
    switch (appointment?.source) {
      case 'ASSOCIATE':
        return 'Booked in App';
      case 'CLIENT':
        return 'Booked in Kiosk';
      default:
        return null;
    }
  })

  const appointmentTimes = useMemo(() => {
    if (!appointmentStore) return [];
    const times = [];
    if (!appointmentDate) return times;

    const dayOfWeek = appointmentDate.day();
    if (disabledDays.includes(dayOfWeek)) return times;

    const openKey = `${week[dayOfWeek]}Open`;
    const closeKey = `${week[dayOfWeek]}Close`;
    const openTime = appointmentStore.hours[openKey];
    const closeTime = appointmentStore.hours[closeKey];

    if (!openTime || !closeTime) return times;

    const now = dayjs(new Date());
    let currentTime = new TZDate(
      appointmentDate.year(),
      appointmentDate.month(),
      appointmentDate.date(),
      parseInt(openTime.slice(0, 2)),
      parseInt(openTime.slice(3, 5)),
      appointmentStore.timezone,
    );

    if (dayjs(currentTime).isBefore(now, 'minute')) {
      currentTime = new TZDate(
        now.year(),
        now.month(),
        now.date(),
        now.hour() + 1,
        now.minute() <= 30 ? 0 : 30,
        appointmentStore.timezone,
      );
    }

    const endTime = dayjs(
      new TZDate(
        appointmentDate.year(),
        appointmentDate.month(),
        appointmentDate.date(),
        parseInt(closeTime.slice(0, 2)),
        parseInt(closeTime.slice(3, 5)),
        appointmentStore.timezone,
      ),
    )
      .add(-30, 'minute')
      .toDate();

    while (currentTime <= endTime) {
      times.push({
        label: currentTime.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }),
        value: currentTime.toISOString(),
      });
      currentTime.setMinutes(currentTime.getMinutes() + 30);
    }
    return times;
  }, [appointmentStore, disabledDays, appointmentDate]);

  useEffect(() => {
    if (appointment) {
      const appStore = appointmentsConfig.stores?.find(app => app.id === appointment.store.id);
      let appDate;
      let appTime;
      if (appStore) {
        appDate = dayjs(new Date(appointment.appointmentDate));
        appTime = formatInTimeZone(appointment.appointmentDate, appStore.timezone, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
      }

      form.setFieldsValue({
        storeId: appStore ? appointment.store.id : null,
        appointmentTypeId: appointment.appointmentType.id,
        appointmentDate: appDate,
        appointmentTime: appTime,
        note: appointment.note,
        associateId: appointment?.associate?.id || userId,
        statusId:
          appointment.status.code === APPOINTMENTS_STATUS.REQUESTED
            ? APPOINTMENTS_STATUS.ASSIGNED
            : appointment.status.code,
      });
    }
  }, [appointment, appointmentsConfig, userId]);

  return (
    <Modal
      okText="Save"
      title={
        appointment
          ? `${appointment?.client?.name || ''}${appointment?.client?.name ? ' - ' : ''}Edit Appointment`
          : 'Add Appointment'
      }
      open={isOpen}
      onCancel={handleCancel}
      onOk={form.submit}
      width={440}
      loading={isLoading}
      confirmLoading={confirmLoading}
      destroyOnClose
    >
      {appointmentSource ? <span className="fs-13 m-btm-12 d-block" style={{ color: '#00000066' }}>{appointmentSource}</span> : null}
      <Form
        form={form}
        onFinish={handleFinish}
        layout="vertical"
        initialValues={{
          statusId: APPOINTMENTS_STATUS.ASSIGNED,
          associateId: userId,
        }}
        requiredMark={label => label}
      >
        <Form.Item
          style={{ marginBottom: '8px' }}
          name="storeId"
          label="Store"
          rules={[{ required: true, message: 'Please select a store' }]}
        >
          <Select
            showSearch
            searchValue={storeSearch}
            onSearch={value => setStoreSearch(value)}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            placeholder="Select Store"
            options={appointmentsConfig?.stores
              ?.filter(store => storesIds.includes(store.id))
              .map(store => ({ label: store.name, value: store.id }))}
            onSelect={value => {
              const associateId = form.getFieldValue('associateId');
              if (value && associateId) {
                const associate = allAssoc.find(assoc => assoc.id === associateId);
                if (associate && !associate.store_ids.includes(value)) {
                  form.setFieldsValue({ associateId: null });
                }
              }
            }}
          />
        </Form.Item>
        <Form.Item
          style={{ marginBottom: '8px' }}
          name="appointmentDate"
          label="Date"
          rules={[{ required: true, message: 'Please select a date' }]}
        >
          <DatePicker
            className="w-100-P"
            disabled={!storeId}
            placeholder="Select Date"
            disabledDate={date => {
              const today = new Date();
              if (date.isBefore(today, 'day')) {
                return true;
              }
              const dayOfWeek = date.day();
              return disabledDays.includes(dayOfWeek);
            }}
            format="MM/DD/YYYY"
            onChange={() => {
              form.setFieldsValue({ appointmentTime: null });
            }}
          />
        </Form.Item>
        <Form.Item
          style={{ marginBottom: '8px' }}
          name="appointmentTime"
          label="Time"
          rules={[{ required: true, message: 'Please select a time' }]}
        >
          <Select placeholder="Select Time" options={appointmentTimes} disabled={!storeId} />
        </Form.Item>
        <Form.Item
          name="appointmentTypeId"
          label="Type"
          rules={[{ required: true, message: 'Please select an appointment type' }]}
          style={{ marginBottom: '8px' }}
        >
          <Select
            showSearch
            searchValue={typeSearch}
            onSearch={value => setTypeSearch(value)}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            placeholder="Select Type"
            options={appointmentsConfig?.appointmentTypes?.map(type => ({ label: type.name, value: type.id }))}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: '8px' }} name="statusId" label="Status">
          <Select
            showSearch
            searchValue={statusSearch}
            onSearch={value => setStatusSearch(value)}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            placeholder="Select Status"
            options={[
              { label: 'Assigned', value: APPOINTMENTS_STATUS.ASSIGNED },
              { label: 'Completed', value: APPOINTMENTS_STATUS.COMPLETED },
              { label: 'Canceled', value: APPOINTMENTS_STATUS.CANCELED },
              { label: 'No Show', value: APPOINTMENTS_STATUS.NO_SHOW },
            ]}
            disabled={!appointment}
          />
        </Form.Item>
        <Form.Item
          name="associateId"
          label="Associate"
          rules={[{ required: true, message: 'Please select an associate' }]}
        >
          <Select
            showSearch
            searchValue={search}
            onSearch={value => setSearch(value)}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            placeholder="Select Associate"
            options={allAssoc
              ?.filter(assoc => assoc.store_ids?.includes(storeId) || !storeId)
              ?.map(assoc => ({ label: `${assoc?.first_name || ''} ${assoc?.last_name || ''}`, value: assoc.id }))}
          />
        </Form.Item>
        <Form.Item name="note" label="Notes" style={{ marginTop: '-15px' }}>
          <TextArea maxLength={500} rows={4} />
        </Form.Item>
        {appointment && appointment.message && (
          <Form.Item label="Message from client" style={{ marginTop: '-15px' }}>
            <TextArea maxLength={500} rows={4} value={appointment.message} disabled style={{ backgroundColor: 'transparent' }} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

function mapStateToProps(state) {
  const { auth, client } = state;

  return {
    userId: auth.userId,
    clientId: client.id,
    storesIds: auth.stores.filter(st => st.id !== 0).map(st => st.id),
  };
}
export default connect(mapStateToProps, {})(AddEditAppointment);
